import React from "react";

const FlashContext = React.createContext(null);

export const FlashProvider = ({ children }) => {
  const [messages, setMessages] = React.useState([]);
  const MAX_TOASTS = 5;

  const AUTO_DISMISS_MS = 5000;

  const lastToastKeyRef = React.useRef({ key: null, at: 0 });
  const DEDUPE_WINDOW_MS = 2000;

  const showToast = React.useCallback(({ type, title, text, autoHideDuration, actionLink }) => {
    const toastKey = `${title ?? ""}|${text ?? ""}|${type ?? ""}`;
    const now = Date.now();
    if (
      lastToastKeyRef.current.key === toastKey &&
      now - lastToastKeyRef.current.at < DEDUPE_WINDOW_MS
    ) {
      return;
    }
    lastToastKeyRef.current = { key: toastKey, at: now };

    const id = (window.crypto && crypto.randomUUID)
      ? crypto.randomUUID()
      : Date.now().toString() + Math.random().toString(36).substring(2);

    let duration = AUTO_DISMISS_MS;
    if (autoHideDuration === null || autoHideDuration === 0) {
      duration = null;
    } else if (typeof autoHideDuration === "number" && autoHideDuration > 0) {
      duration = autoHideDuration;
    }

    setMessages((prev) =>
      [{ id, type, title, text, autoHideDuration: duration, actionLink }, ...prev].slice(0, MAX_TOASTS)
    );
  }, []);

  React.useEffect(() => {
    const pendingToast = sessionStorage.getItem("pendingToast");
    
    if (pendingToast) {
      console.log("CATCHER: Found a pending toast in sessionStorage!", pendingToast);
      
      try {
        const toastData = JSON.parse(pendingToast);
        
        setTimeout(() => {
          showToast(toastData);
          
          sessionStorage.removeItem("pendingToast"); 
          console.log("CATCHER: Toast fired and storage cleaned up.");
        }, 300);

      } catch (e) {
        console.error("Failed to parse pending toast", e);
        sessionStorage.removeItem("pendingToast"); 
      }
    }
  }, [showToast]);

  const dismissToast = (id) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== id));
  };

  const clearAllToasts = () => {
    setMessages([]);
  };

  return (
    <FlashContext.Provider
      value={{
        messages,
        showToast,
        dismissToast,
        clearAllToasts,
      }}
    >
      {children}
    </FlashContext.Provider>
  );
};

export const useFlash = () => {
  const context = React.useContext(FlashContext);
  if (!context) {
    throw new Error("useFlash must be used within a FlashProvider");
  }
  return context;
};